/**
 * Dynamic Pages Hooks
 * React Query hooks for fetching and mutating pages in the builder app.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/hooks/use-toast'
import type { Page, PageSection } from '@limo/database'
import type {
  DynamicPageType,
  PageInsert,
  PageUpdate,
  SectionSettings,
} from '@limo/shared'
import {
  SECTION_REGISTRY,
  getDefaultSectionsForPageType,
  generateUniqueSlug,
} from '@limo/shared'

// =============================================================================
// Types
// =============================================================================

export interface PageWithSections extends Page {
  page_sections: PageSection[]
}

export interface PageWithSectionCount extends Page {
  section_count: number
}

// =============================================================================
// Pages Queries
// =============================================================================

/**
 * Fetch all pages for an account
 */
export function usePages(accountId: string | undefined) {
  return useQuery({
    queryKey: ['pages', accountId],
    queryFn: async (): Promise<PageWithSectionCount[]> => {
      if (!accountId) throw new Error('Account ID required')

      // Fetch pages with section count
      const { data, error } = await supabase
        .from('pages')
        .select(
          `
          *,
          page_sections(count)
        `
        )
        .eq('account_id', accountId)
        .order('page_type', { ascending: true })
        .order('navigation_order', { ascending: true })
        .order('created_at', { ascending: true })

      if (error) throw error

      // Transform the count result
      return (data || []).map((page) => ({
        ...page,
        section_count: (page.page_sections as { count: number }[])?.[0]?.count || 0,
      })) as PageWithSectionCount[]
    },
    enabled: !!accountId,
  })
}

/**
 * Fetch a single page by ID with its sections
 */
export function usePage(pageId: string | undefined) {
  return useQuery({
    queryKey: ['page', pageId],
    queryFn: async (): Promise<PageWithSections> => {
      if (!pageId) throw new Error('Page ID required')

      const { data, error } = await supabase
        .from('pages')
        .select(
          `
          *,
          page_sections(*)
        `
        )
        .eq('id', pageId)
        .single()

      if (error) throw error

      // Sort sections by display_order
      const sections = ((data.page_sections as PageSection[]) || []).sort(
        (a, b) => a.display_order - b.display_order
      )

      return {
        ...data,
        page_sections: sections,
      } as PageWithSections
    },
    enabled: !!pageId,
    refetchOnWindowFocus: true,
  })
}

/**
 * Fetch a page by slug for a specific account
 */
export function usePageBySlug(
  accountId: string | undefined,
  slug: string | undefined
) {
  return useQuery({
    queryKey: ['page-by-slug', accountId, slug],
    queryFn: async (): Promise<Page | null> => {
      if (!accountId || slug === undefined) throw new Error('Account ID and slug required')

      const { data, error } = await supabase
        .from('pages')
        .select('*')
        .eq('account_id', accountId)
        .eq('slug', slug)
        .maybeSingle()

      if (error) throw error
      return data
    },
    enabled: !!accountId && slug !== undefined,
  })
}

// =============================================================================
// Pages Mutations
// =============================================================================

/**
 * Create a new page with default sections
 */
export function useCreatePage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      accountId,
      title,
      slug,
      pageType = 'custom',
      createDefaultSections = true,
    }: {
      accountId: string
      title: string
      slug: string
      pageType?: DynamicPageType
      createDefaultSections?: boolean
    }): Promise<Page> => {
      // Create the page
      const { data: page, error: pageError } = await supabase
        .from('pages')
        .insert({
          account_id: accountId,
          title,
          slug,
          page_type: pageType,
        } satisfies PageInsert)
        .select()
        .single()

      if (pageError) throw pageError

      // Create default sections if requested
      if (createDefaultSections) {
        const defaultSectionTypes = getDefaultSectionsForPageType(pageType)

        const sectionsToInsert = defaultSectionTypes.map((sectionType, index) => {
          const definition = SECTION_REGISTRY[sectionType]
          return {
            account_id: accountId,
            page_id: page.id,
            section_type: sectionType,
            display_order: index,
            is_visible: true,
            settings: definition?.defaultSettings || {},
            content: definition?.defaultContent || {},
          }
        })

        if (sectionsToInsert.length > 0) {
          const { error: sectionsError } = await supabase
            .from('page_sections')
            .insert(sectionsToInsert)

          if (sectionsError) {
            // Rollback: delete the page if sections failed
            await supabase.from('pages').delete().eq('id', page.id)
            throw sectionsError
          }
        }
      }

      return page
    },
    onSuccess: (page) => {
      queryClient.invalidateQueries({
        queryKey: ['pages', page.account_id],
      })
      toast({
        title: 'Page created',
        description: `"${page.title}" has been created successfully.`,
      })
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'An error occurred'
      // Check for unique constraint violation
      if (message.includes('pages_account_slug_unique')) {
        toast({
          title: 'Slug already exists',
          description: 'A page with this URL slug already exists.',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Error creating page',
          description: message,
          variant: 'destructive',
        })
      }
    },
  })
}

/**
 * Update a page's metadata
 */
export function useUpdatePage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      pageId,
      updates,
    }: {
      pageId: string
      updates: PageUpdate
    }): Promise<Page> => {
      const { data, error } = await supabase
        .from('pages')
        .update(updates)
        .eq('id', pageId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (page) => {
      queryClient.invalidateQueries({
        queryKey: ['page', page.id],
      })
      queryClient.invalidateQueries({
        queryKey: ['pages', page.account_id],
      })
      toast({
        title: 'Page updated',
        description: 'Your changes have been saved.',
      })
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'An error occurred'
      if (message.includes('pages_account_slug_unique')) {
        toast({
          title: 'Slug already exists',
          description: 'A page with this URL slug already exists.',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Error updating page',
          description: message,
          variant: 'destructive',
        })
      }
    },
  })
}

/**
 * Toggle a page's published status
 */
export function useTogglePagePublished() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      pageId,
      isPublished,
    }: {
      pageId: string
      isPublished: boolean
    }): Promise<Page> => {
      const { data, error } = await supabase
        .from('pages')
        .update({ is_published: isPublished })
        .eq('id', pageId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (page) => {
      queryClient.invalidateQueries({
        queryKey: ['page', page.id],
      })
      queryClient.invalidateQueries({
        queryKey: ['pages', page.account_id],
      })
      toast({
        title: page.is_published ? 'Page published' : 'Page unpublished',
        description: page.is_published
          ? 'The page is now visible on your website.'
          : 'The page is now hidden from your website.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error updating page',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Delete a page (and its sections via cascade)
 */
export function useDeletePage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      pageId,
      accountId,
    }: {
      pageId: string
      accountId: string
    }): Promise<void> => {
      const { error } = await supabase.from('pages').delete().eq('id', pageId)

      if (error) throw error
    },
    onSuccess: (_, { accountId }) => {
      queryClient.invalidateQueries({
        queryKey: ['pages', accountId],
      })
      toast({
        title: 'Page deleted',
        description: 'The page has been permanently deleted.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error deleting page',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })
}

// =============================================================================
// Home Page Setting
// =============================================================================

/**
 * Fetch the home page ID for an account
 */
export function useHomePage(accountId: string | undefined) {
  return useQuery({
    queryKey: ['home-page', accountId],
    queryFn: async (): Promise<string | null> => {
      if (!accountId) throw new Error('Account ID required')

      const { data, error } = await supabase
        .from('account_settings')
        .select('value')
        .eq('account_id', accountId)
        .eq('key', 'home_page_id')
        .maybeSingle()

      if (error) throw error
      return data?.value || null
    },
    enabled: !!accountId,
  })
}

/**
 * Set the home page for an account
 */
export function useSetHomePage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      accountId,
      pageId,
    }: {
      accountId: string
      pageId: string
    }): Promise<void> => {
      const { error } = await supabase.from('account_settings').upsert(
        {
          account_id: accountId,
          key: 'home_page_id',
          value: pageId,
        },
        {
          onConflict: 'account_id,key',
        }
      )

      if (error) throw error
    },
    onSuccess: (_, { accountId }) => {
      queryClient.invalidateQueries({
        queryKey: ['home-page', accountId],
      })
      queryClient.invalidateQueries({
        queryKey: ['pages', accountId],
      })
      toast({
        title: 'Home page updated',
        description: 'The home page has been changed.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error setting home page',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })
}

// =============================================================================
// Duplicate Page
// =============================================================================

/**
 * Duplicate a page with all its sections
 * Creates a copy of the page with a unique slug and unpublished status.
 */
export function useDuplicatePage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      pageId,
      accountId,
    }: {
      pageId: string
      accountId: string
    }): Promise<Page> => {
      // 1. Fetch the original page
      const { data: originalPage, error: pageError } = await supabase
        .from('pages')
        .select('*')
        .eq('id', pageId)
        .single()

      if (pageError) throw pageError

      // 2. Fetch all sections for this page
      const { data: originalSections, error: sectionsError } = await supabase
        .from('page_sections')
        .select('*')
        .eq('page_id', pageId)
        .order('display_order', { ascending: true })

      if (sectionsError) throw sectionsError

      // 3. Get all existing slugs for this account
      const { data: existingPages, error: slugsError } = await supabase
        .from('pages')
        .select('slug')
        .eq('account_id', accountId)

      if (slugsError) throw slugsError

      const existingSlugs = (existingPages || []).map((p) => p.slug)

      // 4. Generate unique slug
      const newSlug = generateUniqueSlug(originalPage.slug, existingSlugs)

      // 5. Create the new page (unpublished)
      const { data: newPage, error: insertPageError } = await supabase
        .from('pages')
        .insert({
          account_id: accountId,
          slug: newSlug,
          title: `${originalPage.title} (Copy)`,
          page_type: originalPage.page_type,
          meta_title: originalPage.meta_title,
          meta_description: originalPage.meta_description,
          og_image_url: originalPage.og_image_url,
          is_published: false, // Always unpublished
          show_in_navigation: false, // Don't show in nav until intentionally enabled
          show_in_footer: false, // Don't show in footer until intentionally enabled
          navigation_order: originalPage.navigation_order,
          navigation_label: originalPage.navigation_label,
        } satisfies PageInsert)
        .select()
        .single()

      if (insertPageError) throw insertPageError

      // 6. Copy all sections
      if (originalSections && originalSections.length > 0) {
        const sectionsToInsert = originalSections.map((section) => ({
          account_id: accountId,
          page_id: newPage.id,
          section_type: section.section_type,
          display_order: section.display_order,
          is_visible: section.is_visible,
          settings: section.settings,
          content: section.content,
        }))

        const { error: insertSectionsError } = await supabase
          .from('page_sections')
          .insert(sectionsToInsert)

        if (insertSectionsError) {
          // Rollback: delete the new page
          await supabase.from('pages').delete().eq('id', newPage.id)
          throw insertSectionsError
        }
      }

      return newPage
    },
    onSuccess: (page) => {
      queryClient.invalidateQueries({
        queryKey: ['pages', page.account_id],
      })
      toast({
        title: 'Page duplicated',
        description: `"${page.title}" has been created. It's currently unpublished.`,
      })
    },
    onError: (error) => {
      toast({
        title: 'Error duplicating page',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })
}
